import { updateBoundElements } from "./binding";
import { getCommonBoundingBox } from "./bounds";
import { getMaximumGroups } from "./groups";

import type { Scene } from "./Scene";

import type { BoundingBox } from "./bounds";
import type { ExcalidrawElement } from "./types";

export interface Alignment {
  position: "start" | "center" | "end";
  axis: "x" | "y";
}

export const alignElements = (
  selectedElements: ExcalidrawElement[],
  alignment: Alignment,
  scene: Scene,
): ExcalidrawElement[] => {
  const elementsMap = scene.getNonDeletedElementsMap();
  const groups: ExcalidrawElement[][] = getMaximumGroups(
    selectedElements,
    elementsMap,
  );
  const selectionBoundingBox = getCommonBoundingBox(selectedElements);

  return groups.flatMap((group) => {
    const translation = calculateTranslation(
      group,
      selectionBoundingBox,
      alignment,
    );
    return group.map((element) => {
      // update element
      const updatedEle = scene.mutateElement(element, {
        x: element.x + translation.x,
        y: element.y + translation.y,
      });

      // update bound elements
      updateBoundElements(element, scene, {
        simultaneouslyUpdated: group,
      });
      return updatedEle;
    });
  });
};

/**
 * Aligns a single element to its containing frame (similar to Figma behavior)
 */
export const alignElementToFrame = (
  element: ExcalidrawElement,
  frame: ExcalidrawElement,
  alignment: Alignment,
  scene: Scene,
): ExcalidrawElement[] => {
  const elementBounds = getCommonBoundingBox([element]);
  const frameBounds = getCommonBoundingBox([frame]);

  const { axis, position } = alignment;
  const [min, max]: ["minX" | "minY", "maxX" | "maxY"] =
    axis === "x" ? ["minX", "maxX"] : ["minY", "maxY"];

  let newPosition: number;

  if (position === "start") {
    // Align to left/top of frame
    newPosition = frameBounds[min];
  } else if (position === "end") {
    // Align to right/bottom of frame
    newPosition = frameBounds[max] - (elementBounds[max] - elementBounds[min]);
  } else {
    // Center in frame
    const frameCenter = (frameBounds[min] + frameBounds[max]) / 2;
    const elementSize = elementBounds[max] - elementBounds[min];
    newPosition = frameCenter - elementSize / 2;
  }

  const translation = {
    x: axis === "x" ? newPosition - elementBounds[min] : 0,
    y: axis === "y" ? newPosition - elementBounds[min] : 0,
  };

  // Update element position
  const updatedElement = scene.mutateElement(element, {
    x: element.x + translation.x,
    y: element.y + translation.y,
  });

  // Update bound elements
  updateBoundElements(element, scene, {
    simultaneouslyUpdated: [element],
  });

  return [updatedElement];
};

/**
 * Aligns a group of elements to their containing frame (similar to Figma behavior)
 */
export const alignGroupToFrame = (
  elements: ExcalidrawElement[],
  frame: ExcalidrawElement,
  alignment: Alignment,
  scene: Scene,
): ExcalidrawElement[] => {
  const groupBounds = getCommonBoundingBox(elements);
  const frameBounds = getCommonBoundingBox([frame]);

  const { axis, position } = alignment;
  const [min, max]: ["minX" | "minY", "maxX" | "maxY"] =
    axis === "x" ? ["minX", "maxX"] : ["minY", "maxY"];

  let newPosition: number;

  if (position === "start") {
    // Align to left/top of frame
    newPosition = frameBounds[min];
  } else if (position === "end") {
    // Align to right/bottom of frame
    newPosition = frameBounds[max] - (groupBounds[max] - groupBounds[min]);
  } else {
    // Center in frame
    const frameCenter = (frameBounds[min] + frameBounds[max]) / 2;
    const groupSize = groupBounds[max] - groupBounds[min];
    newPosition = frameCenter - groupSize / 2;
  }

  const translation = {
    x: axis === "x" ? newPosition - groupBounds[min] : 0,
    y: axis === "y" ? newPosition - groupBounds[min] : 0,
  };

  // Update all elements in the group
  const updatedElements = elements.map((element) => {
    const updatedElement = scene.mutateElement(element, {
      x: element.x + translation.x,
      y: element.y + translation.y,
    });

    // Update bound elements
    updateBoundElements(element, scene, {
      simultaneouslyUpdated: elements,
    });

    return updatedElement;
  });

  return updatedElements;
};

const calculateTranslation = (
  group: ExcalidrawElement[],
  selectionBoundingBox: BoundingBox,
  { axis, position }: Alignment,
): { x: number; y: number } => {
  const groupBoundingBox = getCommonBoundingBox(group);

  const [min, max]: ["minX" | "minY", "maxX" | "maxY"] =
    axis === "x" ? ["minX", "maxX"] : ["minY", "maxY"];

  const noTranslation = { x: 0, y: 0 };
  if (position === "start") {
    return {
      ...noTranslation,
      [axis]: selectionBoundingBox[min] - groupBoundingBox[min],
    };
  } else if (position === "end") {
    return {
      ...noTranslation,
      [axis]: selectionBoundingBox[max] - groupBoundingBox[max],
    };
  } // else if (position === "center") {
  return {
    ...noTranslation,
    [axis]:
      (selectionBoundingBox[min] + selectionBoundingBox[max]) / 2 -
      (groupBoundingBox[min] + groupBoundingBox[max]) / 2,
  };
};
